// src/pages/Testimonials.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/Testimonials.css";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";

// Firestore
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";

const DEFAULT_CATEGORIES = [
  "Content Strategy",
  "AI Tools Proficiency",
  "Discord Server",
  "UI/UX Design",
  "Presentation Design",
  "Content Writing",
  "Video Editing",
  "Social Media Creatives",
  "Handmade Gifts",
];

// ultra-safe normalizer
const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

async function readMaybeOrdered(colRef) {
  try {
    const qOrdered = query(colRef, orderBy("order", "asc"));
    return await getDocs(qOrdered);
  } catch {
    return await getDocs(colRef);
  }
}

function extractCategories(raw) {
  const one = raw.service ?? raw.category ?? raw.tag ?? raw.type ?? "";
  const many = raw.services ?? raw.categories ?? raw.tags ?? raw.types ?? null;

  let arr = [];
  if (Array.isArray(many)) arr = many;
  else if (typeof many === "string") arr = many.split(",");

  if (one) arr.push(one);

  const seen = new Set();
  return arr
    .map((x) => String(x).trim())
    .filter((x) => x.length)
    .filter((x) => {
      const k = norm(x);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

function coerceRow(raw) {
  const categories = extractCategories(raw);

  const rating =
    typeof raw.rating === "number"
      ? raw.rating
      : Number.isFinite(Number(raw.rating))
      ? Number(raw.rating)
      : undefined;

  const order =
    typeof raw.order === "number"
      ? raw.order
      : Number.isFinite(Number(raw.order))
      ? Number(raw.order)
      : 9999;

  return {
    id: raw.id,
    message: raw.message,
    service: categories[0] || "",
    categories,
    image: raw.image || raw.avatar || "",
    name: raw.name || "Anonymous",
    role: raw.role || raw.title || "",
    rating,
    order,
    active: raw.active !== false,
  };
}

const Testimonials = () => {
  const [page, setPage] = useState({
    heroTitle: "What People Are Saying",
    heroSubtitle:
      "Kind words from those who’ve trusted SuShe to bring their ideas to life.",
    heroBackground: "",
    categoryOrder: DEFAULT_CATEGORIES,
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentIndex, setCurrentIndex] = useState(0);
  const selectedCategory =
    page.categoryOrder?.[currentIndex] ?? DEFAULT_CATEGORIES[0];

  // UI-only form rating
  const [rating, setRating] = useState(0);

  // mobile scroller
  const catRailRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Page settings
        const pageRef = doc(db, "pageContent", "testimonials");
        const pageSnap = await getDoc(pageRef);
        if (mounted && pageSnap.exists()) {
          const d = pageSnap.data() || {};
          const order =
            Array.isArray(d.categoryOrder) && d.categoryOrder.length
              ? d.categoryOrder
              : DEFAULT_CATEGORIES;
          setPage((prev) => ({
            ...prev,
            heroTitle: d.heroTitle || prev.heroTitle,
            heroSubtitle: d.heroSubtitle || prev.heroSubtitle,
            heroBackground: d.heroBackground || prev.heroBackground,
            categoryOrder: order,
          }));
          setCurrentIndex((prevIdx) =>
            Math.min(prevIdx, Math.max(0, order.length - 1))
          );
        }

        // 2) Root collection "testimonials"
        const rootCol = collection(db, "testimonials");
        const snap = await readMaybeOrdered(rootCol);
        if (!mounted) return;

        const raw = [];
        snap.forEach((d) => raw.push({ id: d.id, ...d.data() }));

        const rows = raw
          .map(coerceRow)
          .filter((r) => r.message && r.categories.length && r.active)
          .sort((a, b) =>
            a.order !== b.order
              ? a.order - b.order
              : String(a.name).localeCompare(String(b.name))
          );

        setItems(rows);
        setError("");
      } catch (e) {
        console.error("Testimonials: load failed", e);
        if (mounted) setError("No words yet, but we’re blushing in anticipation! 😊");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // tolerant category match
  const filtered = useMemo(() => {
    const target = norm(selectedCategory);
    return items.filter((t) =>
      t.categories.some((c) => {
        const nc = norm(c);
        return nc === target || nc.includes(target) || target.includes(nc);
      })
    );
  }, [items, selectedCategory]);

  // mobile arrows visibility
  useEffect(() => {
    const el = catRailRef.current;
    if (!el) return;
    const check = () => setShowArrows(el.scrollWidth > el.clientWidth + 4);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    const t = setTimeout(check, 50);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [page.categoryOrder]);

  const handleArrowClick = (dir) => {
    setCurrentIndex((prev) => {
      const next = prev + dir;
      const len = (page.categoryOrder || []).length;
      if (next < 0 || next >= len) return prev;
      return next;
    });
  };

  const handleManualCategorySelect = (cat) => {
    const idx = (page.categoryOrder || []).indexOf(cat);
    if (idx >= 0) setCurrentIndex(idx);
  };

  const bgStyle = page.heroBackground
    ? { backgroundImage: `url(${page.heroBackground})` }
    : {};

  // clamp rating 0..5 for width calc
  const clamp5 = (n) => Math.max(0, Math.min(5, Number(n) || 0));

  return (
    <div className="testimonials-container">
      {/* Hero */}
      <div className="testimonials-hero" style={bgStyle}>
        <div className="testimonials-hero-overlay" />
        <div className="testimonials-hero-text">
          <h1>{page.heroTitle}</h1>
          <p>{page.heroSubtitle}</p>
        </div>
      </div>

      {/* Desktop tabs */}
      <div className="testimonial-tabs">
        {(page.categoryOrder || DEFAULT_CATEGORIES).map((cat, i) => (
          <button
            key={cat}
            className={i === currentIndex ? "active" : ""}
            onClick={() => handleManualCategorySelect(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Mobile category rail */}
      <div className="testimonial-carousel-wrap">
        {showArrows && (
          <button
            className="carousel-arrow left"
            onClick={() => handleArrowClick(-1)}
            disabled={currentIndex === 0}
            aria-label="Previous category"
          >
            ‹
          </button>
        )}
        <div className="testimonial-carousel" ref={catRailRef}>
          {(page.categoryOrder || DEFAULT_CATEGORIES).map((cat, i) => (
            <button
              key={cat}
              className={`pill ${i === currentIndex ? "active" : ""}`}
              onClick={() => handleManualCategorySelect(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        {showArrows && (
          <button
            className="carousel-arrow right"
            onClick={() => handleArrowClick(1)}
            disabled={
              currentIndex ===
              (page.categoryOrder || DEFAULT_CATEGORIES).length - 1
            }
            aria-label="Next category"
          >
            ›
          </button>
        )}
      </div>

      {/* Testimonials grid */}
      <div className="testimonial-grid">
        {loading ? (
          <div className="t-skeleton-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="t-skeleton-card" key={i} />
            ))}
          </div>
        ) : error && items.length === 0 ? (
          <p className="fallback-message">{error}</p>
        ) : filtered.length > 0 ? (
          filtered.map((t) => {
            const pct = (clamp5(t.rating) / 5) * 100 + "%";
            return (
              <div className="t-card" key={t.id || t.message}>
                <blockquote className="t-quote">{t.message}</blockquote>

                <div className="t-footer">
                  {t.image ? (
                    <img
                      className="t-avatar"
                      src={t.image}
                      alt={t.name || "Client"}
                      loading="lazy"
                      onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                    />
                  ) : null}

                  <div className="t-meta">
                    {/* ⭐ Rating now sits above the name */}
                    {typeof t.rating === "number" && (
                      <div className="t-stars t-stars-inline" aria-label={`${t.rating} out of 5`}>
                        <span className="t-stars-outer" aria-hidden="true">
                          <span className="t-stars-base">★★★★★</span>
                          <span
                            className="t-stars-top"
                            style={{ width: pct }}
                          />
                        </span>
                        <span className="t-score">{t.rating.toFixed(1)}</span>
                      </div>
                    )}

                    <div className="t-name">{t.name || "Anonymous"}</div>
                    {t.role ? <div className="t-role">{t.role}</div> : null}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="fallback-message">
            No words yet, but we’re blushing in anticipation! 😊
          </p>
        )}
      </div>

      {/* Form (UI-only) */}
      <div className="testimonial-form">
        <h2>Share Your Experience!</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            alert("Thanks! This form is UI-only right now.");
          }}
        >
          <input type="text" placeholder="Name" />
          <label>Rate the Magic</label>
          <StarRating rating={rating} setRating={setRating} />
          <select defaultValue="">
            <option value="" disabled>
              Select Service
            </option>
            {(page.categoryOrder || DEFAULT_CATEGORIES).map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <textarea placeholder="Your message"></textarea>
          <button type="submit">Submit</button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default Testimonials;
